import { useEffect, useState, useCallback } from 'react';
import { tenantAPI } from '../services/api';
import * as identity from '../identity/identityContract';
import {
  getActiveTenantId,
  setActiveTenantId,
  TENANT_SELECTION_REQUIRED_EVENT,
} from '../services/activeTenant';

/**
 * TenantSwitcher (2026-06-05 tenant-resolution fix; B-2c contract-routed list).
 *
 * - The tenant LIST is a self-service read via identityContract.listMyTenants()
 *   (user-token seam → Library GET /api/tenants). Same shared current_tenant_id;
 *   not a separate source of truth. FAIL-SOFT: if Library is unreachable, the
 *   switcher degrades to current-tenant-only — it never throws or blanks.
 * - The SELECT write stays capture-local (tenantAPI.select → POST
 *   /api/capture/tenants/select, which validates membership server-side).
 * - Single-tenant users: renders nothing (they never need to choose).
 * - Listens for the `tenant_selection_required` signal and forces the chooser open.
 */
export default function TenantSwitcher() {
  const [tenants, setTenants] = useState([]);
  const [currentId, setCurrentId] = useState(getActiveTenantId());
  const [mustSelect, setMustSelect] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await identity.listMyTenants();
    if (r.ok) {
      const { tenants: list = [], currentTenantId } = r.data;
      setTenants(list);
      const resolved = currentTenantId || getActiveTenantId() || null;
      if (resolved && resolved !== getActiveTenantId()) setActiveTenantId(resolved);
      setCurrentId(resolved);
    } else {
      // FAIL-SOFT: Library unreachable/error → degrade to current-tenant-only.
      // Keep whatever active tenant we already have; with <=1 known tenant the
      // switcher hides (existing behavior). Never throw, never blank the page.
      setTenants([]);
      setCurrentId(getActiveTenantId() || null);
    }
  }, []);

  useEffect(() => {
    load();
    const onRequired = () => {
      setMustSelect(true);
      load();
    };
    window.addEventListener(TENANT_SELECTION_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(TENANT_SELECTION_REQUIRED_EVENT, onRequired);
  }, [load]);

  const choose = async (tenantId) => {
    if (!tenantId || busy) return;
    setBusy(true);
    try {
      await tenantAPI.select(tenantId);
      setActiveTenantId(tenantId);
      window.location.reload(); // refetch everything under the new tenant
    } catch {
      setBusy(false);
    }
  };

  // Single-tenant (or not yet loaded with >1): nothing to switch.
  if (tenants.length <= 1 && !mustSelect) return null;

  return (
    <div className="tenant-switcher" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {mustSelect && !currentId && (
        <span style={{ color: '#b45309', fontSize: 13 }}>Choose a workspace:</span>
      )}
      <select
        aria-label="Active workspace"
        value={currentId || ''}
        disabled={busy}
        onChange={(e) => choose(e.target.value)}
      >
        {!currentId && <option value="" disabled>Select workspace…</option>}
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name || t.id}{t.role ? ` (${t.role})` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}
